import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Modal,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../../request/Postrequest";
import { FETCH_CLIENT_JOBS_ACTIVITIES } from "../../../constant/Apipath";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import Loader from "../../../common/Loader";
import { ClientEnglishFallback } from "../../constant/ClientEnglishFallback";

const ClientTimesheetActivitiesModal = ({
  isOpen,
  onClose,
  setSelectedActivityList,
  jobId,
  actList,
  fromJob,
  setOpenjob,
  openEmployee,
  setOpenemployee,
  setSelectEmployees
}) => {
  const [clientJobsActivities, setClientJobsActivities] = useState([]);
  const [searchActivity, setSearchActivity] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);
  const CLIENT_ACCENT_COLOR = selectedClientData?.AccentColor;
  const TOKEN = loginData?.accessToken;
  const USER_ID = loginData?.userInformation?.UserId;

  const {
    TEXT_SELECt_JOB_ACTIVITIES,
    BUTTON_NEXT,
    TEXT_SEARCH_JOB_ACTIVITY,
    TEXT_NO_JOB_ACTIVITES_FOUND
  } = ClientEnglishFallback?.SelectJobActivitiesModal(
    multiLanguageData?.SelectJobActivitiesModal || {}
  );

  const [timeoutId, setTimeoutId] = useState(null);
  const [selectedData, setSelecteddata] = useState([]);

  useEffect(() => {
    // Clear the previous timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    // Set a new timeout for both search and clear
    const id = setTimeout(() => {
      setIsLoading(true);
      getClientJobs();
    }, 500); // Wait 500ms after last keystroke
    setTimeoutId(id);
    // Cleanup function
    return () => clearTimeout(id);
  }, [searchActivity]);

  const getClientJobs = useCallback(async () => {
    try {
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 20,
        FullSearch: searchActivity,
        UserId: USER_ID,
        IncludeRecordNr: true,
        FetchAllowedRecordsOnly: true,
        SearchList: [{ clientJobId: jobId }]
      };
      const clientJobRes = await Postrequestcall(FETCH_CLIENT_JOBS_ACTIVITIES, payload, TOKEN);
      if (clientJobRes.status === 200) {
        const data = clientJobRes?.data?.data || [];
        // Merge selected activities from both actList and selectedData
        const allSelected = [...(actList || []), ...(selectedData || [])];
        const selectedIds = new Set(allSelected.map(item => item?.id));
        
        const processedData = data.map((activity) => ({
          ...activity,
          checked: selectedIds.has(activity?.id)
        }));
        setClientJobsActivities(processedData);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("getClientJobs-> Error", error);
    } finally {
    }
  }, [jobId, searchActivity, TOKEN, USER_ID, actList]);

  // No client-side filtering needed since API handles the search
  const filteredActivities = clientJobsActivities;

  const handleSearchChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/^[^A-Za-z0-9]+/, "");
    setSearchActivity(inputValue);
  };

  const toggleActivitySelection = (activityId) => {
    const getUpdated = clientJobsActivities.map((activity) => {
      if (activity.id === activityId) {
        return {
          ...activity,
          checked: !activity.checked
        };
      }
      return activity;
    });
    setClientJobsActivities(getUpdated);
    
    // Merge current selections with previously selected from actList
    const currentChecked = getUpdated.filter((item) => item?.checked === true);
    const allSelected = [...currentChecked];
    
    // Add items from actList that are not in current view
    const currentIds = new Set(getUpdated.map(item => item.id));
    const previouslySelected = (actList || []).filter(item => 
      !currentIds.has(item.id) && item.checked !== false
    );
    
    setSelecteddata([...allSelected, ...previouslySelected]);
  };

  const handleAddJobs = () => {
    const selectedActivities = clientJobsActivities.filter((activity) => activity.checked);
    setSelectedActivityList(selectedActivities);
    if (openEmployee) {
      setSelectEmployees(true);
      setOpenemployee(false);
    }
    onClose();
    setSearchActivity("");
    setClientJobsActivities([]);
  };

  const handleModalClose = () => {
    onClose();
    if (fromJob === true) {
      setOpenjob(true);
    }
    setSearchActivity("");
    setClientJobsActivities([]);
  };

  React.useEffect(() => {
    if (isOpen === true) {
      getClientJobs();
    }
  }, [isOpen]);

  return (
    <Modal open={isOpen} onClose={() => handleModalClose()}>
      <Box className="client-modal-responsive" sx={{ minHeight: "500px" }}>
        {isLoading ? (
          <Stack minHeight={"500px"} alignItems={"center"} justifyContent={"center"} width={"100%"}>
            <Loader />
          </Stack>
        ) : (
          <>
            <Stack flexDirection={"row"} justifyContent={"space-between"} padding={"5px 0px"}>
              <IconButton
                size="small"
                onClick={() => {
                  handleModalClose();
                }}>
                <ArrowBackIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  handleModalClose();
                }}
                style={{ padding: "4px" }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Stack>
              <Typography variant="h6">{TEXT_SELECt_JOB_ACTIVITIES}</Typography>
            </Stack>
            <TextField
              placeholder={TEXT_SEARCH_JOB_ACTIVITY}
              size="small"
              value={searchActivity}
              sx={{
                backgroundColor: "#e4e4e4",
                borderRadius: "10px",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none"
                },
                marginTop: "10px"
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }
              }}
              fullWidth
              variant="outlined"
              onChange={(e) => handleSearchChange(e)}
            />
            <List sx={{ overflow: "scroll", minHeight: "320px", maxHeight: "400px" }}>
              {filteredActivities.map((activity) => (
                <ListItem
                  key={activity?.id}
                  className="pagelist-content"
                  onClick={() => toggleActivitySelection(activity?.id)}
                  sx={{
                    cursor: "pointer",
                    border: "1px solid rgb(216 216 216)",
                    margin: "10px 0px",
                    borderRadius: "10px",
                    padding: "3px 0px"
                  }}>
                  <Checkbox
                    sx={{
                      "&.Mui-checked": {
                        color: CLIENT_ACCENT_COLOR
                      }
                    }}
                    checked={activity.checked}
                  />
                  <ListItemText
                    primary={`${activity.labourActivityCode} ${activity.labourActivityName}`}
                  />
                </ListItem>
              ))}
              {filteredActivities?.length === 0 && (
                <Stack sx={{ textAlign: "center", justifyContent: "center", minHeight: "350px" }}>
                  <Typography variant="h5" style={{ fontSize: "18px" }}>
                    {TEXT_NO_JOB_ACTIVITES_FOUND}
                  </Typography>
                </Stack>
              )}
            </List>
            {filteredActivities?.length !== 0 && (
              <Button
                sx={{
                  mt: 1,
                  backgroundColor:
                    clientJobsActivities?.filter((activity) => activity.checked)?.length > 0
                      ? CLIENT_ACCENT_COLOR
                      : "lightgrey",
                  color: "white",
                  borderRadius: "10px"
                }}
                fullWidth
                disabled={
                  clientJobsActivities?.filter((activity) => activity.checked)?.length === 0
                    ? true
                    : false
                }
                onClick={handleAddJobs}>
                {BUTTON_NEXT}
              </Button>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default React.memo(ClientTimesheetActivitiesModal);
